import React, { useEffect, useRef, useState } from 'react';
import './Settings.scss';
import ExplainTips from '../../utils/ExplainTips';
import { IOSSwitch } from '../../Switches/Switches';

import Select from 'react-select';

import { SettingService, SettingsKey } from '../../../services/setting-service';
import { WebRTCsdkService } from '../../../services/webrtcsdk-service';
import { PeopleService } from '../../../services/people-service';
import { container } from "tsyringe";

import { ReactComponent as CameraIcon } from '../../../assets/icons/icon-settings-camera.svg';
import { useSharedState } from '../../../utils/helpers';
import { Slider, styled } from '@mui/material';
import { useNotTooFast } from '../../hooks/useNotTooFast';

export interface Option {
  value: string;
  label: string;
}
export const cameraResolutions: readonly Option[] = [
  { value: '720p', label: 'High Definition (720p)' },
  { value: '320p', label: 'Low Res (320p)' },
];
const marks = [
  { value: 1, label: '' },
  { value: 2, label: '' },
  { value: 3, label: '' },
  { value: 4, label: '' },
  { value: 5, label: '' },
  { value: 6, label: '' },
  { value: 7, label: '' },
  { value: 8, label: '' },
  { value: 9, label: '' },
  { value: 10, label: '' }
];

const BlurSlider = styled(Slider)({
  color: '#36db85',
  height: 5,
  '& .MuiSlider-track': {
    border: 'none'
  },
  '& .MuiSlider-thumb': {
    height: 20,
    width: 20,
    backgroundColor: '#fff',
    marginTop: 0,
    marginLeft: 0,
    border: '2px solid currentColor',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&:before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    lineHeight: 1.2,
    fontSize: 12,
    background: 'unset',
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: '#52af77',
    transformOrigin: 'bottom left',
    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
    '&:before': { display: 'none' },
    '&.MuiSlider-valueLabelOpen': {
      transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
    },
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#cccccc',
    height: 5,
    width: 1,
    marginTop: 5,
    '&.MuiSlider-markActive': {
      opacity: 1,
      backgroundColor: 'currentColor',
    },
  },
});


const SettingsCamera = () => {

  // const settings = useSelector((state: any) => state.settings);

  const cameraRef = useRef(null);

  const [settingService] = useState(container.resolve(SettingService));
  const [peopleService] = useState(container.resolve(PeopleService));

  const [webRTCsdkService] = useState(container.resolve(WebRTCsdkService));
  const [localCameras] = useSharedState(webRTCsdkService.localCameras$);

  const [cameraOptions, setCameraOptions] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);

  const [blurRandom, setBlurRandom] = useState(0);

  const [peoples] = useSharedState(peopleService.peoples$);

  const [virtualBlurAvailable, setVirtualBlurAvailable] = useSharedState(webRTCsdkService.virtualBlurAvailable$);
  const [settings, setSettings] = useState({
    blurActive: settingService.getSetting(SettingsKey.cameraBlurActive) && (virtualBlurAvailable === 'ok'),
    blurRange: settingService.getSetting(SettingsKey.cameraBlurRange),
    blurRangeDefault: settingService.getSetting(SettingsKey.cameraBlurRange),
    virtualBgActive: settingService.getSetting(SettingsKey.cameraVirtualBgActive && (virtualBlurAvailable === 'ok')),
  });


  const checkNotTooFast = useNotTooFast(200);

  const onToggleBlur = () => {
    if (checkNotTooFast()) {
      const value = !settings.blurActive;
      const range = settingService.getSetting(SettingsKey.cameraBlurRange);
      setSettings({
        blurActive: value,
        blurRange: range,
        blurRangeDefault: range,
        virtualBgActive: false,
      });
      settingService.setSetting(SettingsKey.cameraBlurActive, value);
      settingService.setSetting(SettingsKey.cameraVirtualBgActive, false);
    }
  }
  const onToggleVirtualBg = () => {
    if (checkNotTooFast()) {
      const value = !settings.virtualBgActive;
      const range = settingService.getSetting(SettingsKey.cameraVirtualBgActive);
      setSettings({
        blurActive: false,
        blurRange: range,
        blurRangeDefault: range,
        virtualBgActive: value,
      });
      settingService.setSetting(SettingsKey.cameraBlurActive, false);
      settingService.setSetting(SettingsKey.cameraVirtualBgActive, value);
    }
  }

  const onChangeBlur = (event, newValue) => {
    setSettings({ ...settings, blurRange: newValue });
    settingService.setSetting(SettingsKey.cameraBlurRange, newValue);
  }

  useEffect(() => {
    webRTCsdkService.handleVirtualBlurBackground(
      settingService.getSetting(SettingsKey.cameraBlurRange) * 2,
      settings.blurActive,
      settings.virtualBgActive,
    );
    setTimeout(() => {
      peopleService.forceUpdateBlur(
        (settings.blurActive ? settingService.getSetting(SettingsKey.cameraBlurRange) : 0) +
        (settings.virtualBgActive ? 20 : 0) );
    }, 1000);
  }, [settings] )

  const onSelectCamera = (value, action) => {
    console.log('camera', value, action);
    setSelectedCamera(value);
    webRTCsdkService.setSelectedCamera(value.value);
    setTimeout(() => {
      peopleService.forceUpdateCamera(value.value);
    }, 1000);
  }
  const onSelectResolution = (value, action) => {
    console.log('resolution', value, action);
    webRTCsdkService.setSelectedResolution(value.value);
  }


  useEffect(() => {
    console.log('CAMERAS', localCameras);
    const options = [];
    if (localCameras.length > 0) {
      localCameras.forEach(c => options.push({ value: c.id, label: c.name }));
      setCameraOptions(options);

      // check if one of the speakers match last used
      let index = 0;
      const lastUsedDevices = JSON.parse(localStorage.getItem("lastUsedDevices") ?? "{}");
      if (lastUsedDevices && lastUsedDevices.camera) {
        index = localCameras.findIndex(s => s.name === lastUsedDevices.camera.name);
      }
      if (index < 0) index = 0;
      setSelectedCamera(options[index]);
      webRTCsdkService.setSelectedCamera(options[index].value);
    }
  }, [localCameras]);

  useEffect(() => {
    webRTCsdkService.attachLocalVideo(cameraRef.current);
  }, [cameraRef]);

  useEffect(() => {
    if (settings.blurActive) {
      // init blur as it is active
      setBlurRandom(Math.random());
    }
  }, [])

  useEffect(() => {
    console.log('virtualBlurAvailable', virtualBlurAvailable);
    if (virtualBlurAvailable != '') {

      if (virtualBlurAvailable === 'notSupported') {
        // clear blur and virtual flags
        setSettings({ ...settings,
          blurActive: false,
          virtualBgActive: false,
        });
        settingService.setSetting(SettingsKey.cameraBlurActive, false);
        settingService.setSetting(SettingsKey.cameraVirtualBgActive, false);
      }
      // reset to default
      setVirtualBlurAvailable('');
    }

  }, [virtualBlurAvailable])

  return (
    <div className="settings-tab">
      <div className="title">
        <CameraIcon className="icon" />
        <div>Camera Settings</div>
      </div>


      <div className="camera-preview">
        <video
          ref={cameraRef}
          autoPlay
        // onLoadedData={initializeBlur}
        />
      </div>

      <ul className="items">

        <li key="camera">
          <div className="container">
            <div className="block">Camera Input
            </div>
          </div>

          <div className="selector">
            <Select
              className="basic-single"
              classNamePrefix="select"
              value={selectedCamera}
              name="quality"
              options={cameraOptions}
              onChange={onSelectCamera}
              isSearchable={false}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: '#f0f0f0',
                  primary50: '#4a4a4a',
                  primary: '#cbd2da',
                },
              })}
            />
          </div>
        </li>

        {/* <li key="resolution">
          <div className="container">
            <div className="block">Video Resolution
            </div>
          </div>

          <div className="selector">
            <Select
              className="basic-single"
              classNamePrefix="select"
              defaultValue={cameraResolutions[0]}
              name="quality"
              options={cameraResolutions}
              onChange={onSelectResolution}
              isSearchable={false}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: '#f0f0f0',
                  primary50: '#4a4a4a',
                  primary: '#cbd2da',
                },
              })}
            />
          </div>
        </li> */}


        {/* <li key="blurBackground">
          <div className="container">
            <div className="block">Background Blur
            </div>
            <div className="block">
              <div>
                <IOSSwitch
                  className='switch'
                  checked={settings?.blurActive || false}
                  onChange={() => onToggleBlur()}
                />
              </div>
            </div>
          </div>
          {settings?.blurActive &&
            <div className="container blur">
              <div className="block">Adjust Blur</div>
              <div className="block">
                <BlurSlider
                  className="blur-range"
                  aria-label="Blur Range"
                  defaultValue={settings.blurRangeDefault}
                  valueLabelDisplay="off"
                  step={null}
                  marks={marks}
                  min={0.8}
                  max={10.2}
                  onChange={onChangeBlur}
                />
              </div>
              <div className="blur-less">Less Blur</div>
              <div className="blur-more">More Blur</div>
            </div>
          }


        </li> */}

        {/* <li key="virtualBackground">
          <div className="container">
            <div className="block">Virtual Background
            </div>
            <div className="block">
              <div>
                <IOSSwitch
                  className='switch'
                  checked={settings?.virtualBgActive || false}
                  onChange={() => onToggleVirtualBg()}
                />
              </div>
            </div>
          </div>
        </li> */}

      </ul>
    </div>
  )

}


export default SettingsCamera;